"use client";

import { useMemo } from "react";
import DeferredKundaliFormCard from "@/app/_home/DeferredKundaliFormCard";
import KundaliSavedLink from "@/app/_home/KundaliSavedLink";
import { getSEOIntentConfigForSource } from "@/lib/seo-intents";
import type { CapturedSEOEntry } from "@/app/_home/KundaliFormCard.parts";
import type { KundaliResponse } from "@/lib/api/kundali";
import type { KundaliLang } from "@/app/_home/KundaliFormCard.i18n";
import CareerSnapshot from "./CareerSnapshot";

/**
 * CareerToolClient — the interactive hero of the dedicated FREE career tool.
 *
 * It REUSES the shared <KundaliFormCard> (no form logic duplicated): the seeker
 * enters birth details → the engine computes the chart → the inline result
 * renders unchanged, including its one intent-matched primary offer. BELOW it
 * we inject only the free 10th-house + career-lord snapshot and saved link.
 *
 * Governance:
 *  - tool_first archetype (rule 43): this form is the page hero, above any offer.
 *  - Single primary CTA (rule 39): KundaliResult owns the sole monetization
 *    component. This adjunct must never inject a second career offer.
 *  - Intent → offer (rule 40): the career SEO intent override makes that shared
 *    KundaliResult placement select `career_report` from governed policy.
 *  - Engine computes / LLM frames (rules 24/29): the snapshot reads engine fields
 *    only; the paid depth is described, never revealed (depth-preview.ts).
 */

const SOURCE_PATH = "/career-astrology";

export default function CareerToolClient() {
  // Career-branched SEO intent config → the form sells career_report and shows
  // career-matched hook/chips. Memoized so the override reference is stable
  // across renders (rule 47 — no unstable prop into the form's effects).
  const seoEntryOverride = useMemo<CapturedSEOEntry>(
    () => ({
      config: getSEOIntentConfigForSource(SOURCE_PATH),
      sourcePath: SOURCE_PATH,
    }),
    []
  );

  return (
    <DeferredKundaliFormCard
      source="career"
      seoEntryOverride={seoEntryOverride}
      showDailyInsightCard={false}
      renderPostResult={(result, language) => (
        <CareerPostResult result={result} language={language} />
      )}
      interactionOnly
    />
  );
}

function CareerPostResult({
  result,
  language,
}: {
  result: KundaliResponse;
  language: KundaliLang;
}) {
  const uiLang: "en" | "hi" = language === "hi" ? "hi" : "en";

  return (
    <div className="mx-auto max-w-md px-4 pt-2 sm:px-6">
      <CareerSnapshot result={result} language={uiLang} />
      {/* Durable return path for the inline kundali (rule 46 — the saved link is
          additive, never dropped). `renderPostResult` REPLACES the default
          KundaliPostResultAdjuncts, so the saved link must be re-rendered here
          or the seeker loses their /kundali/<id> return URL. Guarded on
          result.id exactly like the default adjuncts. */}
      {result.id ? <KundaliSavedLink readingId={result.id} language={language} /> : null}
    </div>
  );
}
